import type { ValueOf } from 'type-fest';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';

import { formatFloatIncludingZero } from 'utils/formatters';
import { mmToInches, poundsToKilograms } from 'utils/helpers';

import { UNITS } from './all';

export const DEFAULT_UNITS_BY_PREFERRED_SYSTEM = {
  [PREFERRED_UNIT_SYSTEMS.IMPERIAL]: UNITS.IN,
  [PREFERRED_UNIT_SYSTEMS.METRIC]: UNITS.MM,
} as const;

export const UNITS_OF_MEASURE = {
  in: 'in.',
  squareIn: 'sq\u202Fin.', // \u202F is a narrow non-breaking space
  cubicIn: 'cu\u202Fin.',
  mm: 'mm',
  squareMm: 'mm²',
  cubicMm: 'mm³',
  lb: 'lb',
  kg: 'kg',
} as const;

export type UnitOfMeasure = ValueOf<typeof UNITS_OF_MEASURE>;

export const MEASURES = {
  LENGTH: 'length',
  AREA: 'area',
  VOLUME: 'volume',
  WEIGHT: 'weight',
} as const;

export type Measure = ValueOf<typeof MEASURES>;

export const MEASURE_TO_DIMENSION = {
  length: 1,
  weight: 1,
  area: 2,
  volume: 3,
} as const satisfies Record<Measure, number>;

export const UNITS_BY_SYSTEM_AND_MEASURE = {
  [PREFERRED_UNIT_SYSTEMS.IMPERIAL]: {
    length: UNITS_OF_MEASURE.in,
    area: UNITS_OF_MEASURE.squareIn,
    volume: UNITS_OF_MEASURE.cubicIn,
    weight: UNITS_OF_MEASURE.lb,
  },
  [PREFERRED_UNIT_SYSTEMS.METRIC]: {
    length: UNITS_OF_MEASURE.mm,
    area: UNITS_OF_MEASURE.squareMm,
    volume: UNITS_OF_MEASURE.cubicMm,
    weight: UNITS_OF_MEASURE.kg,
  },
} as const;

type FormatMeasurementParams = {
  value: number | null;
  unitSystem: PreferredUnitSystem;
  includeUnits?: boolean;
  measure: Measure;
  decimalPoints?: number;
};

export const formatMeasurement = ({
  value,
  unitSystem,
  includeUnits = true,
  measure,
  decimalPoints = 3,
}: FormatMeasurementParams) => {
  if (value === null) {
    return '---';
  }

  let formattedValue: string;

  if (measure === 'weight') {
    // For weight, mmValue is expected to be weight in pounds
    const weightInPounds = value;
    if (unitSystem === PREFERRED_UNIT_SYSTEMS.METRIC) {
      // Convert from pounds to kg
      const weightInKg = poundsToKilograms(weightInPounds);
      formattedValue = formatFloatIncludingZero(weightInKg, decimalPoints);
    } else {
      // Keep in pounds
      formattedValue = formatFloatIncludingZero(weightInPounds, decimalPoints);
    }
  } else {
    // Handle length, area, volume as before
    const dimension = MEASURE_TO_DIMENSION[measure];
    formattedValue =
      unitSystem === PREFERRED_UNIT_SYSTEMS.METRIC
        ? formatFloatIncludingZero(value, decimalPoints)
        : formatFloatIncludingZero(
            mmToInches(value, dimension, decimalPoints),
            decimalPoints,
          );
  }

  if (includeUnits) {
    const unitLabel = UNITS_BY_SYSTEM_AND_MEASURE[unitSystem][measure];
    return `${formattedValue}\u202F${unitLabel}`; // \u202F: narrow non-breaking space for strings
  } else {
    return formattedValue;
  }
};
